import type {
  AgentEdge,
  AgentNode,
  AgentNodeData,
} from '@saaga/shared/interfaces/agency-node.interface'
import type { Node, NodeChange } from '@xyflow/react'
import { applyNodeChanges } from '@xyflow/react'
import { useCallback, useState } from 'react'

import { generateNodeId, getLayoutedElements } from './agency-flow-layout.util'
import { createDefaultNode, createEntryNode } from './agency-flow-node-factory.util'

interface UseAgencyFlowNodesReturn {
  nodeList: AgentNode[]
  setNodeList: React.Dispatch<React.SetStateAction<AgentNode[]>>
  onNodesChange: (changes: NodeChange[]) => void
  addNode: (edgeList: AgentEdge[]) => void
  addNodeWithConnection: (
    position: { x: number; y: number },
    sourceNodeId: string,
    addEdge: (edge: AgentEdge) => void
  ) => void
  updateNodeData: (nodeId: string, data: Partial<AgentNodeData>) => void
}

export const useAgencyFlowNodes = (initialNodeList?: AgentNode[]): UseAgencyFlowNodesReturn => {
  const [nodeList, setNodeList] = useState<AgentNode[]>(() =>
    initialNodeList && initialNodeList.length > 0 ? initialNodeList : [createEntryNode()]
  )

  const onNodesChange = useCallback((changes: NodeChange[]): void => {
    setNodeList((prev) => applyNodeChanges(changes, prev as Node[]) as AgentNode[])
  }, [])

  const addNode = useCallback((edgeList: AgentEdge[]): void => {
    const newNode = createDefaultNode()
    setNodeList((prev) => {
      const { nodeList: layouted } = getLayoutedElements([...prev, newNode], edgeList)
      return layouted
    })
  }, [])

  const addNodeWithConnection = useCallback(
    (
      position: { x: number; y: number },
      sourceNodeId: string,
      addEdge: (edge: AgentEdge) => void
    ): void => {
      const newNodeId = generateNodeId()
      const newNode = createDefaultNode(position)
      newNode.id = newNodeId
      setNodeList((prev) => [...prev, newNode])
      addEdge({
        id: `e-${sourceNodeId}-${newNodeId}`,
        source: sourceNodeId,
        target: newNodeId,
        label: '',
      })
    },
    []
  )

  const updateNodeData = useCallback((nodeId: string, data: Partial<AgentNodeData>): void => {
    setNodeList((prev) =>
      prev.map((node) => (node.id !== nodeId ? node : { ...node, data: { ...node.data, ...data } }))
    )
  }, [])

  return { nodeList, setNodeList, onNodesChange, addNode, addNodeWithConnection, updateNodeData }
}
